/**
 * gratis-probe.js
 * Adds the free probe when the eligible cart total reaches 80 EUR.
 * Remembers an externally removed probe in localStorage for that cart.
 * Automatic removal by this script does not count as declining the gift.
 */
(function () {
  const GRATIS_PROBE_VARIANT_ID = 51088475095306;
  const THRESHOLD_CENTS = 18000; // 180 EUR
  const PROBE_PROPERTY = '_gift';
  const DIGITAL_TAG = 'digital';
  const EXCLUDED_PRODUCT_IDS = [10595615932682];
  const ENABLE_GRATIS_PROBE = true;

  if (!ENABLE_GRATIS_PROBE) return;

  const ROOT = window.Shopify.routes.root;
  const SOURCE = 'gratis-probe';
  const declinedInMemory = new Set();

  let previousCartToken = null;
  let previouslyHadProbe = null;
  let externalRevision = 0;

  // Keep the original eligibility treatment of items marked as gifts.
  function isProbeItem(item) {
    return (
      item.properties &&
      Object.prototype.hasOwnProperty.call(item.properties, PROBE_PROPERTY)
    );
  }

  // Only track/add/remove this particular promotional probe.
  function isGratisProbeItem(item) {
    return (
      Number(item.variant_id) === GRATIS_PROBE_VARIANT_ID &&
      isProbeItem(item)
    );
  }

  function declinedKey(cart) {
    return `gratis-probe:declined:${GRATIS_PROBE_VARIANT_ID}:${cart.token}`;
  }

  function probeWasDeclined(cart) {
    const key = declinedKey(cart);

    if (declinedInMemory.has(key)) return true;

    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  function markProbeDeclined(cart) {
    const key = declinedKey(cart);
    declinedInMemory.add(key);

    try {
      localStorage.setItem(key, '1');
    } catch {
      // If storage is blocked, remember the choice until this page closes.
    }
  }

  function rememberCart(cart) {
    previousCartToken = cart.token;
    previouslyHadProbe = cart.items.some(isGratisProbeItem);
  }

  function observeCart(cart) {
    const hasProbe = cart.items.some(isGratisProbeItem);

    if (
      previousCartToken === cart.token &&
      previouslyHadProbe === true &&
      !hasProbe
    ) {
      markProbeDeclined(cart);
    }

    rememberCart(cart);
  }

  async function fetchCart() {
    const response = await fetch(`${ROOT}cart.js`, {
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Cart fetch failed: HTTP ${response.status}`);
    }

    const cart = await response.json();

    if (!Array.isArray(cart.items) || !cart.token) {
      throw new Error('Invalid cart response');
    }

    return cart;
  }

  async function allNonProbeItemsAreExcludedOrDigital(items) {
    const nonProbeItems = items.filter((item) => !isProbeItem(item));

    if (nonProbeItems.length === 0) return false;

    const itemsToCheck = nonProbeItems.filter(
      (item) => !EXCLUDED_PRODUCT_IDS.includes(Number(item.product_id))
    );

    if (itemsToCheck.length === 0) return true;

    const uniqueHandles = [
      ...new Set(itemsToCheck.map((item) => item.handle)),
    ];

    try {
      const products = await Promise.all(
        uniqueHandles.map(async (handle) => {
          const response = await fetch(`${ROOT}products/${handle}.js`);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          return response.json();
        })
      );

      return products.every((product) => {
        const tags = Array.isArray(product.tags)
          ? product.tags
          : String(product.tags || '').split(',');

        return tags.some(
          (tag) =>
            String(tag).trim().toLowerCase() === DIGITAL_TAG.toLowerCase()
        );
      });
    } catch (error) {
      console.error(
        '[GratisProbe] Could not check product eligibility:',
        error
      );

      // Preserve the original fail-open behavior.
      return false;
    }
  }

  async function addProbe() {
    const response = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: GRATIS_PROBE_VARIANT_ID,
        quantity: 1,
        properties: {
          [PROBE_PROPERTY]: 'magnesium mini',
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Add probe failed: HTTP ${response.status}`);
    }
  }

  async function removeProbe(probeItem) {
    const response = await fetch(`${ROOT}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: probeItem.key,
        quantity: 0,
      }),
    });

    if (!response.ok) {
      throw new Error(`Remove probe failed: HTTP ${response.status}`);
    }

    const cart = await response.json();

    if (cart.errors || !Array.isArray(cart.items) || !cart.token) {
      throw new Error('Remove probe returned an invalid cart or an error');
    }

    return cart;
  }

  async function evaluateCart() {
    const revision = externalRevision;

    // Read current state instead of replaying potentially stale event payloads.
    const cart = await fetchCart();
    observeCart(cart);

    const probeItem = cart.items.find(isGratisProbeItem);
    const hasNonProbeItems = cart.items.some((item) => !isProbeItem(item));
    const meetsLimit = (cart.total_price || 0) >= THRESHOLD_CENTS;

    const allExcludedOrDigital = hasNonProbeItems
      ? await allNonProbeItemsAreExcludedOrDigital(cart.items)
      : false;

    // A newer external event queued another evaluation while we were waiting.
    if (revision !== externalRevision) return;

    const shouldHaveProbe =
      hasNonProbeItems && meetsLimit && !allExcludedOrDigital;

    if (shouldHaveProbe && !probeItem && !probeWasDeclined(cart)) {
      await addProbe();

      // Record our successful addition even if the follow-up fetch fails.
      previousCartToken = cart.token;
      previouslyHadProbe = true;

      const updatedCart = await fetchCart();
      observeCart(updatedCart);

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: SOURCE,
        cartData: updatedCart,
      });
    } else if (!shouldHaveProbe && probeItem) {
      const updatedCart = await removeProbe(probeItem);

      // Our own removal is not a customer decline.
      rememberCart(updatedCart);

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: SOURCE,
        cartData: updatedCart,
      });
    }
  }

  // Establish the initial baseline before processing external cart updates.
  // Initialization itself does not add/remove gifts.
  let queue = fetchCart()
    .then(rememberCart)
    .catch((error) => {
      console.error('[GratisProbe] Initialization failed:', error);
    });

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.source === SOURCE) return;

    externalRevision += 1;

    // Serialize evaluations rather than dropping events during our own requests.
    queue = queue
      .then(evaluateCart)
      .catch((error) => {
        console.error('[GratisProbe] Cart evaluation failed:', error);
      });
  });
})();

// Fügt das Gratis-Mini-Produkt hinzu, wenn die URL "gratis_mini" enthält.
async function handleGratisMini() {
  const GRATIS_MINI_VARIANT_ID = 51088475095306;

  // Nur ausführen, wenn "gratis_mini" in der URL enthalten ist.
  if (!window.location.href.includes('gratis_mini')) {
    return;
  }

  try {
    // Aktuellen Warenkorb laden.
    const cartResponse = await fetch(
      `${window.Shopify.routes.root}cart.js`
    );

    if (!cartResponse.ok) {
      throw new Error(`Cart fetch failed: HTTP ${cartResponse.status}`);
    }

    const cart = await cartResponse.json();

    // Prüfen, ob das Gratis Mini bereits im Warenkorb ist.
    const hasGratisMini = cart.items.some(
      (item) => item.variant_id === GRATIS_MINI_VARIANT_ID
    );

    if (hasGratisMini) {
      console.log('[GratisMini] Bereits im Warenkorb.');
      return;
    }

    // Gratis Mini hinzufügen.
    const addResponse = await fetch(
      `${window.Shopify.routes.root}cart/add.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: GRATIS_MINI_VARIANT_ID,
              quantity: 1,
            },
          ],
        }),
      }
    );

    if (!addResponse.ok) {
      throw new Error(`Add failed: HTTP ${addResponse.status}`);
    }

    // Warenkorb nach dem Hinzufügen erneut laden.
    const updatedCartResponse = await fetch(
      `${window.Shopify.routes.root}cart.js`
    );

    if (!updatedCartResponse.ok) {
      throw new Error(
        `Updated cart fetch failed: HTTP ${updatedCartResponse.status}`
      );
    }

    const updatedCart = await updatedCartResponse.json();

    // Dawn Cart-Bubble aktualisieren.
    updateDawnCartBubble(updatedCart.item_count);

    console.log('[GratisMini] Gratis Mini hinzugefügt.');
  } catch (error) {
    console.error('[GratisMini] Fehler:', error);
  }
}

/**
 * Aktualisiert den Cart Count im Shopify Dawn Header.
 */
function updateDawnCartBubble(itemCount) {
  const cartIcon = document.getElementById('cart-icon-bubble');

  if (!cartIcon) {
    console.warn('[GratisMini] #cart-icon-bubble nicht gefunden.');
    return;
  }

  let bubble = cartIcon.querySelector('.cart-count-bubble');

  // Wenn der Warenkorb vorher leer war, existiert die Bubble noch nicht.
  if (!bubble && itemCount > 0) {
    bubble = document.createElement('div');
    bubble.classList.add('cart-count-bubble');

    bubble.innerHTML = `
      <span aria-hidden="true"></span>
      <span class="visually-hidden"></span>
    `;

    cartIcon.appendChild(bubble);
  }

  if (!bubble) {
    return;
  }

  const visibleCount = bubble.querySelector('[aria-hidden="true"]');
  const hiddenCount = bubble.querySelector('.visually-hidden');

  if (visibleCount) {
    visibleCount.textContent = itemCount < 100 ? itemCount : '';
  }

  if (hiddenCount) {
    hiddenCount.textContent = `${itemCount} Artikel im Warenkorb`;
  }
}

// Beim Seitenaufruf prüfen.
handleGratisMini();